import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Calendar,
  MapPin,
  Sparkles,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import {
  getMarketplaceProfile,
  createRequirement,
  updateRequirement,
  getMyRequirements,
  MarketplaceRequirement,
  RequirementType,
  MarketplaceProfile,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import BrandDatePicker from "../../atoms/Utlis/BrandDatePicker";
import { fadeUp, isProfileCompleted } from "./MarketplaceShared";
import {
  DEFAULT_REQUIREMENT_FORM,
  MARKETPLACE_GRADIENT,
  REQUIREMENT_STATUS_BADGE_STYLES,
} from "../../organisms/Dashboard/Marketplace/marketplaceConstants";
import MarketplaceLoader from "./MarketplaceLoader";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";

interface ReqForm {
  requirement_type: RequirementType;
  title: string;
  description: string;
  location: string;
  date_from: string;
  date_to: string;
  budget: string;
  status: "active" | "draft";
}

const defaultForm: ReqForm = DEFAULT_REQUIREMENT_FORM;

type Step = "details" | "technical" | "review";

const MarketplacePostRequirementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  const [form, setForm] = useState<ReqForm>(defaultForm);
  const [editTarget, setEditTarget] = useState<MarketplaceRequirement | null>(null);
  const [myReqs, setMyReqs] = useState<MarketplaceRequirement[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [myProfile, setMyProfile] = useState<MarketplaceProfile | null>(null);

  // Step state
  const [currentStep, setCurrentStep] = useState<Step>("details");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/marketplace");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      }, 100);
      return;
    }
    getMarketplaceProfile()
      .then((res) => {
        const profileData = res?.data || null;
        if (!isProfileCompleted(profileData)) {
          navigate("/marketplace/profile/create");
        } else {
          setMyProfile(profileData);
          localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
          setAuthChecking(false);
        }
      })
      .catch(() => {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const prof = parsed?.data || parsed;
            if (isProfileCompleted(prof)) {
              setMyProfile(prof);
              setAuthChecking(false);
              return;
            }
          } catch {}
        }
        navigate("/marketplace/profile/create");
      });
  }, []);

  const fetchMyReqs = async () => {
    try {
      const res = await getMyRequirements({ page_size: 50 });
      setMyReqs(res.results ?? []);
    } catch {
      setMyReqs([]);
    }
  };

  useEffect(() => {
    if (!authChecking) fetchMyReqs();
  }, [authChecking]);

  useEffect(() => {
    if (!authChecking && editId && myReqs.length > 0) {
      const found = myReqs.find((r) => r.id === editId);
      if (found) startEdit(found);
    }
  }, [authChecking, editId, myReqs.length]);

  const set = (key: keyof ReqForm, val: string) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const startEdit = (req: MarketplaceRequirement) => {
    setEditTarget(req);
    setForm({
      requirement_type: req.requirement_type,
      title: req.title,
      description: req.description || "",
      location: req.location || "",
      date_from: req.date_from || "",
      date_to: req.date_to || "",
      budget: req.budget || "",
      status: (req.status as "active" | "draft") || "active",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot post or update requirements.");
      return;
    }
    if (!form.title.trim()) {
      showErrorToast("Title is required");
      return;
    }
    setSubmitting(true);

    try {
      const payload: Partial<MarketplaceRequirement> = {
        requirement_type: form.requirement_type,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        location: form.location.trim() || undefined,
        date_from: form.date_from || undefined,
        date_to: form.date_to || undefined,
        budget: form.budget || undefined,
        status: form.status,
      };
      if (editTarget) {
        await updateRequirement(editTarget.id, payload);
        sendFirebaseEvent('Marketplace_Requirement_Updated', 'Marketplace', form.requirement_type, editTarget.id);
        showSuccessToast("Requirement updated!");
        setEditTarget(null);
      } else {
        await createRequirement(payload);
        sendFirebaseEvent('Marketplace_Requirement_Posted', 'Marketplace', form.requirement_type, `budget:${!!form.budget},location:${!!form.location.trim()}`);
        showSuccessToast("Requirement posted!");
      }
      setForm(defaultForm);
      setCurrentStep("details");
      navigate("/marketplace/my-listings");
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || "Failed to post requirement");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setForm(defaultForm);
    setCurrentStep("details");
    navigate("/marketplace/my-listings");
  };

  if (authChecking) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <PageLayout>
      {/* ── TOP MULTI-STEP NAVIGATION WIZARD ── */}
      <StepsNavbar>
        <NavbarContent>
          <NavbarSteps>
            <StepIndicator
              $active={currentStep === "details"}
              $completed={currentStep === "technical" || currentStep === "review"}
              onClick={() => setCurrentStep("details")}
            >
              <span>1</span> Project Details
            </StepIndicator>
            <StepConnector />
            <StepIndicator
              $active={currentStep === "technical"}
              $completed={currentStep === "review"}
              onClick={() => setCurrentStep("technical")}
            >
              <span>2</span> Technical &amp; Creative
            </StepIndicator>
            <StepConnector />
            <StepIndicator
              $active={currentStep === "review"}
              $completed={false}
              onClick={() => setCurrentStep("review")}
            >
              <span>3</span> Review
            </StepIndicator>
          </NavbarSteps>
        </NavbarContent>
      </StepsNavbar>

      <ContentWrap>
        <TwoPanelLayout>
          {/* Form Panel */}
          <FormPanel onSubmit={handleSubmit}>
            {myProfile?.is_active === false && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 18px",
                  marginBottom: "20px",
                  borderRadius: "10px",
                  background: "rgba(239, 68, 68, 0.12)",
                  border: "1px solid rgba(239, 68, 68, 0.35)",
                  color: "#fca5a5",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                <AlertTriangle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
                <div>
                  <strong>Account Frozen:</strong> Your marketplace account has been deactivated/frozen by an administrator.
                  You cannot post new requirements or update existing requirements.
                </div>
              </div>
            )}

            {/* STEP 1: Project Details */}
            {currentStep === "details" && (
              <>
                <HeroHeader>
                  <HeroTitle>
                    Create a New <GradientText>{form.requirement_type === "service_requirement" ? "Production Call" : "Equipment Call"}</GradientText>
                  </HeroTitle>
                  <HeroSubtitle>
                    Connect with the industry's most vetted technical talent and specialized gear owners. Detail your vision below to attract the right creative partners.
                  </HeroSubtitle>
                </HeroHeader>

                <Section>
                  <SectionHeader>Requirement Type Selection</SectionHeader>
                  <TypeSelectionRow>
                    <TypeBadgeButton
                      type="button"
                      $active={form.requirement_type === "service_requirement"}
                      onClick={() => set("requirement_type", "service_requirement")}
                    >
                      Post a Job (Talent)
                    </TypeBadgeButton>
                    <TypeBadgeButton
                      type="button"
                      $active={form.requirement_type === "equipment_requirement"}
                      onClick={() => set("requirement_type", "equipment_requirement")}
                    >
                      Post Equipment Requirement
                    </TypeBadgeButton>
                  </TypeSelectionRow>
                </Section>

                {/* 01. Essential Information */}
                <SectionCard>
                  <SectionTitle>01. Essential Information</SectionTitle>

                  <FormGroup>
                    <Label>
                      {form.requirement_type === "equipment_requirement" ? "Equipment Title" : "Job Title"}
                    </Label>
                    <Input
                      value={form.title}
                      onChange={(e) => set("title", e.target.value)}
                      placeholder={
                        form.requirement_type === "equipment_requirement"
                          ? "e.g. Sony FX3 camera package needed for commercial shoot"
                          : "e.g. Wedding photographer needed for Dec 15"
                      }
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Primary Location</Label>
                    <InputWithIcon>
                      <MapPin size={15} color="var(--mp-textColor)" style={{ opacity: 0.6 }} />
                      <InputNoStyle
                        value={form.location}
                        onChange={(e) => set("location", e.target.value)}
                        placeholder="City, State or Region"
                      />
                    </InputWithIcon>
                  </FormGroup>
                </SectionCard>

                {/* 02. Compensation & Timeline */}
                <SectionCard>
                  <SectionTitle>02. Compensation &amp; Timeline</SectionTitle>

                  <TwoCol>
                    <FormGroup>
                      <Label>Budget limit (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.budget}
                        onChange={(e) => set("budget", e.target.value)}
                        placeholder="e.g. 50000"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Budget Status</Label>
                      <Select
                        value={form.status}
                        onChange={(e) => set("status", e.target.value as "active" | "draft")}
                      >
                        <option value="active">Active (Visible)</option>
                        <option value="draft">Draft (Private)</option>
                      </Select>
                    </FormGroup>
                  </TwoCol>

                  <TwoCol>
                    <FormGroup>
                      <Label>From Date</Label>
                      <BrandDatePicker
                        value={form.date_from}
                        onChange={(v) => set("date_from", v)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>To Date</Label>
                      <BrandDatePicker
                        value={form.date_to}
                        onChange={(v) => set("date_to", v)}
                      />
                    </FormGroup>
                  </TwoCol>
                </SectionCard>

                <FormFooter>
                  <MutedTextButton onClick={cancelEdit} type="button">Discard</MutedTextButton>
                  <SubmitBtn type="button" onClick={() => setCurrentStep("technical")}>
                    Next Step: Technical &amp; Creative
                  </SubmitBtn>
                </FormFooter>
              </>
            )}

            {/* STEP 2: Technical & Creative */}
            {currentStep === "technical" && (
              <>
                <HeroHeader>
                  <HeroTitle>03. Technical &amp; Creative</HeroTitle>
                  <HeroSubtitle>
                    Outline any specific technical needs or reference files to align expectations.
                  </HeroSubtitle>
                </HeroHeader>

                <SectionCard>
                  <FormGroup>
                    <Label>Technical Requirements &amp; Description</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="Describe your requirement in detail..."
                      rows={8}
                    />
                  </FormGroup>
                </SectionCard>

                <FormFooter>
                  <MutedTextButton onClick={() => setCurrentStep("details")} type="button">Back</MutedTextButton>
                  <SubmitBtn type="button" onClick={() => setCurrentStep("review")}>
                    Review Listing
                  </SubmitBtn>
                </FormFooter>
              </>
            )}

            {/* STEP 3: Review */}
            {currentStep === "review" && (
              <>
                <HeroHeader>
                  <HeroTitle>Verify &amp; Publish</HeroTitle>
                  <HeroSubtitle>
                    Review your requirement listing details before publishing it live.
                  </HeroSubtitle>
                </HeroHeader>

                <SectionCard>
                  <SectionTitle>Summary Review</SectionTitle>
                  <ReviewItem>
                    <strong>Title:</strong> {form.title || "—"}
                  </ReviewItem>
                  <ReviewItem>
                    <strong>Location:</strong> {form.location || "—"}
                  </ReviewItem>
                  <ReviewItem>
                    <strong>Budget limit:</strong> {form.budget ? `₹${form.budget}` : "Not specified"}
                  </ReviewItem>
                  <ReviewItem>
                    <strong>Status:</strong> {form.status === "active" ? "Active" : "Draft"}
                  </ReviewItem>
                  {form.date_from && (
                    <ReviewItem>
                      <strong>From Date:</strong> {form.date_from}
                    </ReviewItem>
                  )}
                  {form.date_to && (
                    <ReviewItem>
                      <strong>To Date:</strong> {form.date_to}
                    </ReviewItem>
                  )}
                  {form.description && (
                    <ReviewItem>
                      <strong>Description:</strong> {form.description}
                    </ReviewItem>
                  )}
                </SectionCard>

                <FormFooter>
                  <MutedTextButton onClick={() => setCurrentStep("technical")} type="button">Back</MutedTextButton>
                  <FooterRightBtns>
                    <DraftBtn
                      type="button"
                      disabled={submitting || myProfile?.is_active === false}
                      onClick={() => { set("status", "draft"); setTimeout(() => document.getElementById("req-submit-btn")?.click(), 50); }}
                      style={{
                        opacity: myProfile?.is_active === false ? 0.5 : 1,
                        cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                      }}
                    >
                      {submitting ? "Saving…" : "Save as Draft"}
                    </DraftBtn>
                    <SubmitBtn
                      id="req-submit-btn"
                      type="submit"
                      disabled={submitting || myProfile?.is_active === false}
                      style={{
                        opacity: myProfile?.is_active === false ? 0.5 : 1,
                        cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                      }}
                    >
                      {submitting ? "Publishing…" : "Publish Requirement"}
                    </SubmitBtn>
                  </FooterRightBtns>
                </FormFooter>
              </>
            )}
          </FormPanel>

          {/* Right Panel: Pro Tips & Preview */}
          <RightPanel>
            {/* Pro Tips Box */}
            <ProTipsCard>
              <ProTipsHeader>
                <Sparkles size={16} color="var(--mp-textColor)" />
                Pro Tips
              </ProTipsHeader>

              <TipItem>
                <TipIndex>TIP #01</TipIndex>
                <TipTitle>Be Specific About Gear</TipTitle>
                <TipDesc>Don't just say "Camera." Mention the mount, sensor preference, or essential accessories. Professionals value clarity.</TipDesc>
              </TipItem>

              <TipItem>
                <TipIndex>TIP #02</TipIndex>
                <TipTitle>Moodboards Matter</TipTitle>
                <TipDesc>Visuals bridge the gap between technical specs and creative vision. Uploading at least 3 images increases high-tier applicants by 40.</TipDesc>
              </TipItem>

              <TipItem>
                <TipIndex>TIP #03</TipIndex>
                <TipTitle>Realistic Budgeting</TipTitle>
                <TipDesc>Top-tier talent often looks for posts with transparent ranges. If the rate is negotiable based on kit, mention it explicitly.</TipDesc>
              </TipItem>
            </ProTipsCard>
          </RightPanel>
        </TwoPanelLayout>
      </ContentWrap>
    </PageLayout>
  );
};

export default MarketplacePostRequirementPage;

// ─── Styled Components ─────────────────────────────────────────────────────────

const PageLayout = styled.div`
  min-height: calc(100vh - 64px);
  width: 100%;
  background: var(--mp-bodyBackground, #f7f8fc);
  font-family: Urbanist, sans-serif;
`;

const StepsNavbar = styled.div`
  background: var(--mp-cardBackground, white);
  border-bottom: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  position: sticky;
  top: 64px;
  z-index: 10;
  padding: 14px 24px;

  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const NavbarContent = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const NavbarSteps = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 8px;
  }
`;

const StepIndicator = styled.button<{ $active: boolean; $completed: boolean }>`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: ${p => p.$active ? "var(--mp-textColor, #171717)" : p.$completed ? "var(--mp-textColor, #171717)" : "var(--mp-subColor, rgba(23,23,23,0.4))"};
  opacity: ${p => p.$active || p.$completed ? 1 : 0.6};

  @media (max-width: 576px) {
    font-size: 11px;
    gap: 4px;
  }

  span {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => p.$active ? "var(--mp-iconGrad, linear-gradient(135deg, #FFD573 0%, #D863BA 100%))" : p.$completed ? "var(--mp-textColor, #171717)" : "transparent"};
    color: ${p => p.$active ? "white" : p.$completed ? "var(--mp-cardBackground, white)" : "inherit"};
    border: 1.5px solid ${p => p.$completed || p.$active ? "transparent" : "var(--mp-border, rgba(23,23,23,0.2))"};
    font-size: 11px;
    font-weight: 700;
    transition: all 0.2s;
  }
`;

const StepConnector = styled.div`
  width: 32px;
  height: 2px;
  background: var(--mp-border, rgba(23,23,23,0.08));

  @media (max-width: 576px) {
    display: none;
  }
`;

const NavbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MutedTextButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--mp-subColor, rgba(23, 23, 23, 0.6));
  padding: 8px 14px;
  border-radius: 8px;
  &:hover { background: var(--mp-bodyBackground, rgba(23,23,23,0.04)); }
`;

const DraftBtn = styled.button`
  background: var(--mp-cardBackground, white);
  border: 1.5px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 8px;
  padding: 8px 16px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--mp-textColor, #171717);
  cursor: pointer;
  &:hover { background: var(--mp-bodyBackground, #f7f8fc); }
`;

const NavbarPrimaryBtn = styled.button`
  background: var(--mp-iconGrad, linear-gradient(135deg, #FFD573 0%, #D863BA 100%));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(216, 99, 186, 0.15);
  &:hover { opacity: 0.95; }
`;

const ContentWrap = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 32px 40px 80px;
  box-sizing: border-box;
  animation: ${fadeUp} 0.35s ease both;

  @media (max-width: 768px) {
    padding: 16px 12px 40px;
  }
`;

const TwoPanelLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 32px;
  align-items: start;
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
`;

const FormPanel = styled.form`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeroHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  color: var(--mp-textColor, #171717);
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const GradientText = styled.span`
  background: var(--mp-iconGrad, linear-gradient(135deg, #FFC951 0%, #C94DAA 100%));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HeroSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-subColor, rgba(23,23,23,0.5));
  line-height: 1.5;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--mp-subColor, rgba(23, 23, 23, 0.45));
`;

const TypeSelectionRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const TypeBadgeButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: 1.5px solid ${p => p.$active ? "transparent" : "var(--mp-border, rgba(23,23,23,0.12))"};
  background: ${p => p.$active ? "var(--mp-iconGrad, linear-gradient(135deg, #FFD573 0%, #D863BA 100%))" : "var(--mp-cardBackground, white)"};
  color: ${p => p.$active ? "white" : "var(--mp-subColor, rgba(23,23,23,0.7))"};
`;

const SectionCard = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  border-radius: 16px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px;
  font-family: Montserrat, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: var(--mp-textColor, #171717);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--mp-subColor, rgba(23,23,23,0.5));
`;

const Input = styled.input`
  height: 44px;
  padding: 0 16px;
  border: 1.5px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: var(--mp-textColor, #171717);
  background: var(--mp-bodyBackground, #f7f8fc);
  transition: all 0.15s;
  &:focus { outline: none; border-color: var(--mp-textColor, #171717); }
`;

const Select = styled.select`
  height: 44px;
  padding: 0 16px;
  border: 1.5px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: var(--mp-textColor, #171717);
  background: var(--mp-bodyBackground, #f7f8fc);
  cursor: pointer;
`;

const InputWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border: 1.5px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 10px;
  background: var(--mp-bodyBackground, #f7f8fc);
`;

const InputNoStyle = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: var(--mp-textColor, #171717);
  background: transparent;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const Textarea = styled.textarea`
  padding: 14px;
  border: 1.5px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: var(--mp-textColor, #171717);
  background: var(--mp-bodyBackground, #f7f8fc);
  resize: vertical;
  min-height: 120px;
  &:focus { outline: none; border-color: var(--mp-textColor, #171717); }
`;

const ReviewItem = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--mp-textColor, #171717);
  padding: 8px 0;
  border-bottom: 1px solid var(--mp-border, rgba(23,23,23,0.06));
  &:last-child { border: none; }
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;

  @media (max-width: 576px) {
    flex-direction: column-reverse;
    gap: 12px;
    align-items: stretch;

    button {
      width: 100%;
      text-align: center;
    }
  }
`;

const FooterRightBtns = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;

    button {
      width: 100%;
    }
  }
`;

const SubmitBtn = styled.button`
  background: var(--mp-iconGrad, linear-gradient(135deg, #FFD573 0%, #D863BA 100%));
  color: white;
  border: none;
  border-radius: 9px;
  padding: 12px 24px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(216, 99, 186, 0.2);
  transition: all 0.2s;
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// Pro Tips & Preview Panel

const ProTipsCard = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProTipsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--mp-textColor, #171717);
`;

const TipItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TipIndex = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: var(--mp-textColor, #171717);
  opacity: 0.55;
`;

const TipTitle = styled.h4`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-textColor, #171717);
`;

const TipDesc = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--mp-subColor, rgba(23, 23, 23, 0.6));
  line-height: 1.5;
`;

const SafePaymentsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--mp-subColor, rgba(23,23,23,0.5));
`;

const PreviewCard = styled.div`
  background: var(--mp-cardBackground, #15181b);
  border: 1px solid var(--mp-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: Montserrat, sans-serif;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--mp-subColor, rgba(255,255,255,0.4));
`;

const PreviewStatus = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f59e0b;
`;

const PreviewTitle = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: var(--mp-textColor, white);
  line-height: 1.3;
`;

const PreviewMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--mp-subColor, rgba(255,255,255,0.6));
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 120px 20px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: var(--mp-subColor, rgba(23, 23, 23, 0.4));
`;
